<script lang="ts">
  import AdminShell from "@/components/AdminShell.svelte";
  import { api, ApiError, type AdminServer } from "@/api";
  import { token, user } from "@/stores/session";
  import { route, routeHref } from "@/router";
  import { confirmModal } from "@/stores/modal";
  import { coreOptions, statusLabels, transitionalStatuses } from "@/config";
  import CoreVersionSelect from "@/components/CoreVersionSelect.svelte";
  import Button from "@/components/Button.svelte";
  import Select from "@/components/Select.svelte";
  import Card from "@/components/Card.svelte";
  import Badge from "@/components/Badge.svelte";
  import Alert from "@/components/Alert.svelte";
  import AdminInventorySelect from "@/components/AdminInventorySelect.svelte";

  import LiveConsole from "@/components/LiveConsole.svelte";
  import FileManager from "@/components/FileManager.svelte";
  import ServerTelemetry from "@/components/ServerTelemetry.svelte";
  import BackupPanel from "@/components/BackupPanel.svelte";
  import FormField from "@/components/FormField.svelte";
  import FormGrid from "@/components/FormGrid.svelte";
  import LoadingState from "@/components/LoadingState.svelte";
  import TextInput from "@/components/TextInput.svelte";
  import NumberInput from "@/components/NumberInput.svelte";
  import {
    connect as connectConsole,
    disconnect as disconnectConsole,
    consoleStatus,
  } from "@/stores/console";
  import { copyToClipboard } from "@/utils/clipboard";
  import { startTransitionalPoll } from "@/utils/transitional-poll";
  import { formatDateTime } from "@/utils/format";

  type AdminTab = "console" | "files" | "players" | "metrics" | "backups" | "settings";

  let server:
    (AdminServer & { diskLimitMb: number; playerLimit: number; cpuLimitPercent: number }) | null =
    $state(null);
  let loading = $state(true);
  let busy = $state(false);
  let activeTab = $state<AdminTab>("console");

  let saveSuccess = $state(false);
  let error = $state<string | null>(null);
  const isAdmin = $derived($user?.role === "admin");

  const backendAddr = $derived.by(() => {
    if (!server) {
      return "";
    }
    if (server.backendAddress) {
      return server.backendAddress;
    }
    if (server.nodeIp && server.hostPort) {
      return `${server.nodeIp}:${server.hostPort}`;
    }
    if (server.hostPort) {
      return `${server.nodeName ?? "localhost"}:${server.hostPort}`;
    }
    return "";
  });

  const proxyAddr = $derived.by(() => {
    if (!server) {
      return "";
    }
    return server.joinAddress || "";
  });
  let copiedBackend = $state(false);
  let copiedProxy = $state(false);

  // Форма редактирования параметров сервера
  let editForm = $state({
    name: "",
    core: "paper",
    version: "1.21.4",
    memLimitMb: 1024,
    diskLimitMb: 1024,
    cpuLimitPercent: 100,
    playerLimit: 5,
    nodeId: "",
  });

  async function load() {
    if (!$token || !$route.args.id) {
      return;
    }
    loading = true;
    try {
      const [s] = await Promise.all([api.adminServer($token, $route.args.id)]);
      server = s;
      editForm = {
        name: s.name,
        core: s.core,
        version: s.version,
        memLimitMb: s.memLimitMb,
        diskLimitMb: s.diskLimitMb,
        cpuLimitPercent: s.cpuLimitPercent,
        playerLimit: s.playerLimit,
        nodeId: s.nodeId ?? "",
      };
      error = null;
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Не удалось загрузить сервер";
    } finally {
      loading = false;
    }
  }

  async function saveServerDetails() {
    if (!$token || !server || busy) {
      return;
    }
    if (!editForm.name.trim()) {
      error = "Имя сервера не может быть пустым";
      return;
    }

    const coreChanged = editForm.core !== server.core;
    const versionChanged = editForm.version.trim() !== server.version;
    const wipeRequired = coreChanged || versionChanged;

    if (wipeRequired) {
      const confirmed = await confirmModal(
        "Внимание: смена сборки/версии",
        "При смене сборки или версии все текущие файлы сервера будут очищены, а контейнер переустановлен. Продолжить?",
        "Переустановить с очисткой",
      );
      if (!confirmed) {
        return;
      }
    }

    busy = true;
    error = null;
    saveSuccess = false;
    try {
      await api.adminUpdateServer($token, server.id, {
        name: editForm.name.trim(),
        core: editForm.core,
        version: editForm.version.trim(),
        memLimitMb: Number(editForm.memLimitMb),
        diskLimitMb: Number(editForm.diskLimitMb),
        cpuLimitPercent: Number(editForm.cpuLimitPercent),
        playerLimit: Number(editForm.playerLimit),
        nodeId: editForm.nodeId || null,
        confirmWipe: wipeRequired ? true : undefined,
      });
      saveSuccess = true;
      setTimeout(() => (saveSuccess = false), 3000);
      await load();
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Не удалось обновить сервер";
    } finally {
      busy = false;
    }
  }

  async function action(type: "start" | "stop" | "restart" | "kill" | "delete") {
    if (!$token || !server || busy) {
      return;
    }
    const deletionForbidden = type === "delete" && !isAdmin;
    if (deletionForbidden) {
      return;
    }
    const labels = {
      start: { title: "Запустить сервер?", confirm: "Запустить" },
      stop: { title: "Остановить сервер?", confirm: "Остановить" },
      restart: { title: "Перезапустить сервер?", confirm: "Перезапустить" },
      kill: { title: "Аварийно остановить сервер?", confirm: "Аварийно остановить" },
      delete: { title: "Удалить сервер? Операция необратима.", confirm: "Удалить" },
    };
    if (
      !(await confirmModal(
        labels[type].title,
        `${server.name} · ${server.ownerUsername}`,
        labels[type].confirm,
      ))
    ) {
      return;
    }
    busy = true;
    error = null;
    try {
      if (type === "start") {
        await api.adminStartServer($token, server.id);
      } else if (type === "stop") {
        await api.adminStopServer($token, server.id);
      } else if (type === "restart") {
        await api.adminRestartServer($token, server.id);
      } else if (type === "kill") {
        await api.adminKillServer($token, server.id);
      } else {
        await api.adminDeleteServer($token, server.id);
      }
      await load();
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Операция не выполнена";
    } finally {
      busy = false;
    }
  }

  async function copyText(text: string, type: "backend" | "proxy") {
    if (!text) {
      return;
    }
    if (!(await copyToClipboard(text))) {
      error = "Не удалось скопировать адрес. Выделите его и скопируйте вручную.";
      return;
    }
    if (type === "backend") {
      copiedBackend = true;
      setTimeout(() => (copiedBackend = false), 2500);
    } else {
      copiedProxy = true;
      setTimeout(() => (copiedProxy = false), 2500);
    }
  }

  function selectTab(tab: AdminTab) {
    activeTab = tab;
  }

  function handleEditFormSubmit(event: SubmitEvent) {
    event.preventDefault();
    void saveServerDetails();
  }

  $effect(() => {
    void load();
  });

  // Подключение консоли при открытой вкладке "console"
  $effect(() => {
    if (!server || activeTab !== "console" || !$token) {
      disconnectConsole();
      return;
    }
    connectConsole(server.id, $token);
    return () => disconnectConsole();
  });

  // Авто-опрос в переходных состояниях
  $effect(() => {
    if (!server || !transitionalStatuses.has(server.status)) {
      return;
    }
    const poll = startTransitionalPoll({
      request: async () => {
        await load();
        return true;
      },
      intervalMs: 4000,
    });
    return () => poll.stop();
  });

  // Автоматическое обновление статуса при остановке/смене состояния через live-событие
  $effect(() => {
    if ($consoleStatus && server && server.status !== $consoleStatus) {
      server = { ...server, status: $consoleStatus };
      void load();
    }
  });
</script>

<AdminShell section="servers">
  <div class="admin-top-nav">
    <a class="back-link" href={routeHref("admin-servers")}>← Все серверы</a>
    {#if server}
      <a
        class="button button-ghost open-user-view"
        href={routeHref("server-home", { id: server.id })}
      >
        Открыть в панели пользователя ↗
      </a>
    {/if}
  </div>

  {#if error}
    <Alert type="error">
      {error}
      {#if !server}<button
          class="ghost-button"
          type="button"
          onclick={() => void load()}
          disabled={loading}>Повторить загрузку</button
        >{/if}
    </Alert>
  {/if}
  {#if saveSuccess}
    <Alert type="success">Параметры сервера успешно обновлены.</Alert>
  {/if}

  {#if loading && !server}
    <LoadingState message="Загрузка сервера…" />
  {:else if server}
    <Card class="admin-card detail-card">
      <div class="detail-heading">
        <div>
          <p class="eyebrow">Управление чужим сервером · #{server.id.slice(0, 8)}</p>
          <h2>{server.name}</h2>
          <p class="muted">
            Владелец:
            <a class="table-link" href={routeHref("admin-user", { id: server.ownerId })}>
              {server.ownerUsername}
            </a>
            · {server.game} ({server.core}
            {server.version})
          </p>
        </div>
        <Badge status={server.status}>{statusLabels[server.status] ?? server.status}</Badge>
      </div>

      <div class="connection-section">
        {#if backendAddr}
          <div class="connection-bar backend-bar">
            <div class="connection-info">
              <span class="connection-label">Запущен на (IP:порт / resolve backend):</span>
              <code>{backendAddr}</code>
            </div>
            <Button
              variant="ghost"
              size="compact"
              onclick={() => void copyText(backendAddr, "backend")}
            >
              {copiedBackend ? "Скопировано!" : "Копировать IP:порт"}
            </Button>
          </div>
        {:else}
          <div class="connection-bar dim-bar">
            <span class="connection-label">Запущен на (IP:порт):</span>
            <span class="muted">Не запущен на ноде / порт не выделен</span>
          </div>
        {/if}

        {#if proxyAddr}
          <div class="connection-bar proxy-bar">
            <div class="connection-info">
              <span class="connection-label">Публичный адрес (антибот):</span>
              <code>{proxyAddr}</code>
            </div>
            <Button
              variant="ghost"
              size="compact"
              onclick={() => void copyText(proxyAddr, "proxy")}
            >
              {copiedProxy ? "Скопировано!" : "Копировать адрес"}
            </Button>
          </div>
        {/if}
      </div>

      {#if server.lastError}
        <Alert type="error">{server.lastError}</Alert>
      {/if}

      <!-- Оперативные действия администратора -->
      <div class="admin-actions">
        {#if ["stopped", "failed"].includes(server.status)}
          <Button variant="primary" onclick={() => void action("start")} disabled={busy}>
            Запустить
          </Button>
        {/if}
        {#if ["running", "starting"].includes(server.status)}
          <Button variant="ghost" onclick={() => void action("stop")} disabled={busy}>
            Остановить
          </Button>
        {/if}
        {#if server.status === "running"}
          <Button variant="secondary" onclick={() => void action("restart")} disabled={busy}>
            Перезапустить
          </Button>
        {/if}
        {#if ["running", "starting", "stopping", "queued_stop", "queued_start", "failed"].includes(server.status)}
          <Button variant="danger" onclick={() => void action("kill")} disabled={busy}>
            Аварийно остановить
          </Button>
        {/if}

        {#if isAdmin}
          <Button variant="danger" onclick={() => void action("delete")} disabled={busy}>
            Удалить сервер
          </Button>
        {/if}
      </div>

      <!-- Вкладки управления -->
      <nav class="admin-server-tabs" aria-label="Разделы управления сервером">
        <button
          type="button"
          class:active={activeTab === "console"}
          onclick={() => selectTab("console")}
        >
          Консоль
        </button>
        <button
          type="button"
          class:active={activeTab === "files"}
          onclick={() => selectTab("files")}
        >
          Файлы
        </button>
        <button
          type="button"
          class:active={activeTab === "players"}
          onclick={() => selectTab("players")}
        >
          Игроки
        </button>
        <button
          type="button"
          class:active={activeTab === "metrics"}
          onclick={() => selectTab("metrics")}
        >
          Метрики
        </button>
        <button
          type="button"
          class:active={activeTab === "backups"}
          onclick={() => selectTab("backups")}
        >
          Бэкапы
        </button>
        <button
          type="button"
          class:active={activeTab === "settings"}
          onclick={() => selectTab("settings")}
        >
          Параметры и ресурсы
        </button>
      </nav>

      <!-- Содержимое активной вкладки -->
      <div class="tab-content">
        {#if activeTab === "console"}
          <div class="tab-pane">
            <LiveConsole />
          </div>
        {:else if activeTab === "files"}
          <div class="tab-pane">
            <FileManager
              serverId={server.id}
              disabled={transitionalStatuses.has(server.status)}
              serverStatus={server.status}
            />
          </div>
        {:else if activeTab === "players"}
          <div class="tab-pane">
            <ServerTelemetry serverId={server.id} status={server.status} part="players" />
          </div>
        {:else if activeTab === "metrics"}
          <div class="tab-pane">
            <ServerTelemetry serverId={server.id} status={server.status} part="metrics" />
          </div>
        {:else if activeTab === "backups"}
          <div class="tab-pane">
            <BackupPanel
              serverId={server.id}
              serverStatus={server.status}
              enabled={server.backupsEnabled}
              slots={server.backupSlots ?? (server.backupsEnabled ? 1 : 0)}
              onUpdated={load}
            />
          </div>
        {:else if activeTab === "settings"}
          <div class="tab-pane">
            <dl class="detail-facts">
              <div>
                <dt>Последняя резервная копия</dt>
                <dd>
                  {server.backupHealth?.status ?? "нет данных"}{server.backupHealth?.purpose
                    ? ` · ${server.backupHealth.purpose}`
                    : ""}
                </dd>
              </div>
            </dl>
            <!-- Текущие факты -->
            <dl class="detail-facts">
              <div>
                <dt>Backend (IP:порт)</dt>
                <dd>
                  {#if server.nodeIp && server.hostPort}
                    <code>{server.nodeIp}:{server.hostPort}</code>
                  {:else if server.hostPort}
                    <code>:{server.hostPort}</code>
                  {:else}
                    не запущен
                  {/if}
                </dd>
              </div>
              <div>
                <dt>Игра</dt>
                <dd>{server.game}</dd>
              </div>
              <div>
                <dt>Сборка</dt>
                <dd>{server.core} {server.version}</dd>
              </div>
              <div>
                <dt>Нода и IP</dt>
                <dd>
                  {#if server.nodeId}
                    <a class="table-link" href={routeHref("admin-node", { id: server.nodeId })}>
                      {server.nodeName ?? "перейти"}
                    </a>
                    {#if server.nodeIp}
                      <small class="dim">({server.nodeIp})</small>
                    {/if}
                  {:else}
                    {server.nodeName ?? "не назначена"}
                  {/if}
                </dd>
              </div>
              <div>
                <dt>Порт на хосте</dt>
                <dd>{server.hostPort ?? "не назначен"}</dd>
              </div>
              <div>
                <dt>Код входа (joinCode)</dt>
                <dd><code>{server.joinCode ?? "—"}</code></dd>
              </div>
              <div>
                <dt>Память (ОЗУ)</dt>
                <dd>{server.memLimitMb} МБ</dd>
              </div>
              <div>
                <dt>Диск</dt>
                <dd>{server.diskLimitMb} МБ</dd>
              </div>
              <div>
                <dt>Лимит игроков</dt>
                <dd>{server.playerLimit}</dd>
              </div>
              <div>
                <dt>Лимит CPU</dt>
                <dd>{server.cpuLimitPercent}%</dd>
              </div>
              <div>
                <dt>Резервные копии</dt>
                <dd>
                  {server.backupsEnabled
                    ? `включены (слотов: ${server.backupSlots ?? 1})`
                    : "не куплены"}
                </dd>
              </div>
              <div>
                <dt>Создан</dt>
                <dd>{formatDateTime(server.createdAt)}</dd>
              </div>
            </dl>

            <!-- Форма редактирования параметров сервера администратором -->
            <div class="card-edit-section">
              <h3>Редактирование параметров сервера</h3>
              <p class="muted">
                Административное изменение выделенных ресурсов и назначенной ноды без списания
                кредитов.
              </p>

              <form class="server-edit-form" onsubmit={handleEditFormSubmit}>
                <FormGrid>
                  <FormField label="Имя сервера"
                    ><TextInput
                      bind:value={editForm.name}
                      required
                      maxlength={48}
                      disabled={busy}
                    /></FormField
                  >
                  <FormField label="Сборка сервера">
                    <Select
                      aria-label="Сборка сервера"
                      bind:value={editForm.core}
                      disabled={busy}
                      options={coreOptions}
                    />
                  </FormField>
                  <FormField label="Версия игры"
                    ><CoreVersionSelect
                      core={editForm.core}
                      bind:value={editForm.version}
                      disabled={busy}
                      preserveUnavailable
                    /></FormField
                  >
                </FormGrid>

                <FormGrid>
                  <FormField label="Память (ОЗУ, МБ)"
                    ><NumberInput
                      bind:value={editForm.memLimitMb}
                      min="256"
                      max="32768"
                      step="256"
                      required
                      disabled={busy}
                    /></FormField
                  >
                  <FormField label="Диск (МБ)"
                    ><NumberInput
                      bind:value={editForm.diskLimitMb}
                      min="512"
                      max="131072"
                      step="512"
                      required
                      disabled={busy}
                    /></FormField
                  >
                  <FormField label="Лимит CPU (%)"
                    ><NumberInput
                      bind:value={editForm.cpuLimitPercent}
                      min="10"
                      max="800"
                      step="10"
                      required
                      disabled={busy}
                    /></FormField
                  >
                  <FormField label="Лимит игроков"
                    ><NumberInput
                      bind:value={editForm.playerLimit}
                      min="1"
                      max="200"
                      required
                      disabled={busy}
                    /></FormField
                  >
                </FormGrid>

                <FormGrid>
                  <FormField label="Назначенная нода">
                    <AdminInventorySelect
                      label="Назначенная нода"
                      bind:value={editForm.nodeId}
                      disabled={busy}
                      filters={{}}
                      selectedLabel={server.nodeName ?? ""}
                      emptyLabel="Назначить автоматически"
                    />
                  </FormField>
                </FormGrid>

                {#if isAdmin}
                  <Button type="submit" disabled={busy || !editForm.name.trim()}>
                    {busy ? "Сохраняем…" : "Сохранить параметры сервера"}
                  </Button>
                {:else}
                  <p class="muted">Только администратор может сохранять изменения конфигурации.</p>
                {/if}
              </form>
            </div>
          </div>
        {/if}
      </div>
    </Card>
  {/if}
</AdminShell>

<style>
  .admin-top-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .open-user-view {
    text-decoration: none;
    font-size: 0.85rem;
  }

  .connection-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 1.25rem 0;
  }

  .connection-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.65rem 1rem;
    background: var(--bg-subtle, rgba(255, 255, 255, 0.02));
    border: 1px solid var(--line);
    border-radius: var(--radius-sm, 6px);
    flex-wrap: wrap;
  }

  .backend-bar {
    background: rgba(226, 91, 48, 0.06);
    border-color: rgba(226, 91, 48, 0.3);
  }

  .connection-info {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
  }

  .connection-label {
    font-size: 0.85rem;
    color: var(--text-muted);
  }

  .connection-bar code {
    font-family: var(--font-mono, monospace);
    font-size: 0.95rem;
    color: var(--text-main);
    user-select: all;
    background: var(--bg-card);
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    border: 1px solid var(--line);
  }

  .admin-server-tabs {
    display: flex;
    gap: 0.5rem;
    margin: 1.5rem 0 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--line);
    overflow-x: auto;
  }

  .admin-server-tabs button {
    background: transparent;
    border: none;
    color: var(--text-muted);
    font-size: 0.9rem;
    font-weight: 500;
    padding: 0.5rem 1rem;
    border-radius: var(--radius-sm, 6px);
    cursor: pointer;
    white-space: nowrap;
    transition:
      background 0.15s,
      color 0.15s;
  }

  .admin-server-tabs button:hover {
    color: var(--text-main);
    background: var(--bg-hover, rgba(255, 255, 255, 0.05));
  }

  .admin-server-tabs button.active {
    color: #fff;
    background: var(--primary, #e25b30);
  }

  .tab-pane {
    padding-top: 0.5rem;
  }

  .detail-facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
    margin: 1rem 0 1.5rem;
    padding: 1.25rem;
    background: var(--bg-subtle, rgba(255, 255, 255, 0.02));
    border: 1px solid var(--line);
    border-radius: var(--radius-sm, 6px);
  }

  .detail-facts dt {
    font-size: 0.75rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.25rem;
  }

  .detail-facts dd {
    font-size: 0.95rem;
    font-weight: 500;
    margin: 0;
  }

  .card-edit-section {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--line);
  }

  .card-edit-section h3 {
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }

  .server-edit-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 800px;
    margin-top: 1rem;
  }
</style>
